import cx from 'classnames';

import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
// @ts-expect-error - no TypeScript definitions yet
import { FieldSelect } from 'src/components/FieldSelect';
// @ts-expect-error - no TypeScript definitions yet
import { FieldSelectMenuItem } from 'src/components/FieldSelectMenuItem';
import type { BackendOAuthConsentWorkspace } from 'src/types/backend';

import {
  getEligibleCustomers,
  getEligibleWorkspaces,
  getMcpIneligibleReasonLabel,
  groupWorkspacesByCustomer,
} from '../../lib';

import { useHandlers } from './useHandlers';
import { useViewState } from './useViewState';
import styles from './ViewWorkspacePicker.module.scss';

interface ViewWorkspacePickerProps {
  className?: string;
  workspaces: BackendOAuthConsentWorkspace[];
}

export const ViewWorkspacePicker = ({ className, workspaces }: ViewWorkspacePickerProps) => {
  const { selectedCustomerId, selectedWorkspaceId, pendingDecision } = useViewState();
  const { handleCustomerChange, handleWorkspaceChange } = useHandlers();

  const eligibleWorkspaces = getEligibleWorkspaces(workspaces);

  if (eligibleWorkspaces.length === 0) {
    return null;
  }

  if (eligibleWorkspaces.length === 1) {
    const [workspace] = eligibleWorkspaces;

    return (
      <CustomTypography
        className={cx(styles.root, className)}
        variant={TYPOGRAPHY_VARIANT.regular14}
        data-testid="oauthConsentSingleWorkspace"
      >
        Workspace: {workspace.customer.name} / {workspace.name}
      </CustomTypography>
    );
  }

  const customers = groupWorkspacesByCustomer(workspaces);
  const showCustomerSelect = getEligibleCustomers(workspaces).length > 1;
  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId);
  const selectedWorkspace = eligibleWorkspaces.find(
    (workspace) => workspace.id === selectedWorkspaceId
  );
  const disabled = pendingDecision !== null;

  return (
    <div className={cx(styles.root, className)}>
      {showCustomerSelect && (
        <FieldSelect
          label="Account"
          placeholder="Choose an account"
          value={selectedCustomer?.id ?? ''}
          renderValue={() => selectedCustomer?.name ?? ''}
          onChange={handleCustomerChange}
          disabled={disabled}
          dataTestId="oauthConsentCustomerSelect"
        >
          {customers.map((customer) => (
            <FieldSelectMenuItem
              key={customer.id}
              value={customer.id}
              disabled={customer.eligibleWorkspaces.length === 0}
              data-testid={`oauthConsentCustomer_${customer.id}`}
            >
              <span className={styles.optionName}>{customer.name}</span>
              {customer.ineligibleReason && (
                <span className={styles.optionReason}>
                  {getMcpIneligibleReasonLabel(customer.ineligibleReason)}
                </span>
              )}
            </FieldSelectMenuItem>
          ))}
        </FieldSelect>
      )}

      <FieldSelect
        label="Workspace"
        placeholder="Choose a workspace"
        value={selectedWorkspace?.id ?? ''}
        renderValue={() => selectedWorkspace?.name ?? ''}
        onChange={handleWorkspaceChange}
        disabled={disabled || !selectedCustomer}
        dataTestId="oauthConsentWorkspaceSelect"
      >
        {(selectedCustomer?.eligibleWorkspaces ?? []).map((workspace) => (
          <FieldSelectMenuItem
            key={workspace.id}
            value={workspace.id}
            data-testid={`oauthConsentWorkspace_${workspace.id}`}
          >
            {workspace.name}
          </FieldSelectMenuItem>
        ))}
      </FieldSelect>
    </div>
  );
};
